/**
 * Exterior One / Exterior Two payout split (Nashville exterior paint).
 *
 * The two percentages say how much of a job's Exterior Siding/Paint amount is
 * paid at each exterior stage, so they must split the amount exactly: the
 * pair totals 100% and neither leg may take everything or nothing. Region
 * and Fixed Subdivision rows store the absolute pair; Offset Subdivision rows
 * store signed deltas that are added to the Region pair before the rule runs.
 *
 * Values are compared in integer hundredths so 33.33 + 66.67 passes and
 * floating-point noise never blocks a save. Shared by the API subscribers and
 * the admin forms so both show the same message.
 */

export const EXTERIOR_PAYOUT_TOTAL_MESSAGE =
  'Unable to save. The Exterior One and Exterior Two percentages must equal 100%.';

export const EXTERIOR_PAYOUT_BOUNDS_MESSAGE =
  'Unable to save. The resulting Exterior One and Exterior Two percentages cannot be 0% or 100%.';

const HUNDRED_PERCENT_IN_HUNDREDTHS = 10_000;

/**
 * A percentage as integer hundredths (90 -> 9000, "33.33" -> 3333), or null
 * when there is no usable number. TypeORM returns decimals as strings, so
 * strings are accepted as well.
 */
export const toHundredths = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = Number(value);

  return Number.isFinite(parsed) ? Math.round(parsed * 100) : null;
};

/**
 * Validates a resolved Exterior One / Exterior Two pair given in hundredths.
 * Returns the message to show, or null when the pair is acceptable. The total
 * is checked first, so a pair that fails both rules (0/0, 100/100) reports
 * the total; a pair that totals 100 but parks on an edge (100/0, 110/-10)
 * reports the bounds.
 */
export const exteriorPayoutError = (
  exteriorOne: number | null,
  exteriorTwo: number | null,
): string | null => {
  if (
    exteriorOne === null ||
    exteriorTwo === null ||
    exteriorOne + exteriorTwo !== HUNDRED_PERCENT_IN_HUNDREDTHS
  ) {
    return EXTERIOR_PAYOUT_TOTAL_MESSAGE;
  }

  const isOutOfBounds = (leg: number) =>
    leg <= 0 || leg >= HUNDRED_PERCENT_IN_HUNDREDTHS;

  if (isOutOfBounds(exteriorOne) || isOutOfBounds(exteriorTwo)) {
    return EXTERIOR_PAYOUT_BOUNDS_MESSAGE;
  }

  return null;
};
